import React, { useState, useEffect } from "react";
import { Level, PromotionRule } from "../../../../services/Dtos";
import { VStack, HStack, Text, Box, Badge, Icon, Tabs } from "@chakra-ui/react";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import CustomAlert from "../../../CustomUi/CustomAlert";
import useThemeColors from "../../../../hooks/useThemeColors";
import useIcons from "../../../../hooks/useIcons";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import SelectInput from "../../../CustomUi/SelectInput";
import CustomNumberInput from "../../../CustomUi/CustomNumberInput";
import CustomSwitch from "../../../CustomUi/CustomSwitch";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useLevelService } from "../../../../hooks/Domain/useLevelService";
import InlineSeparator from "../../../CustomUi/InlineSeparator";
import Info from "../../../CustomUi/Info";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  levelToEdit: Level | null;
  nextOrder: number;
  activeTab: number;
  setActiveTab: (tab: number) => void;
}

const LevelModal: React.FC<Props> = ({
  isOpen,
  onClose,
  levelToEdit,
  nextOrder,
  activeTab,
  setActiveTab,
}) => {
  const {
    createLevelWithPromotionRule,
    updateLevelWithPromotionRule,
    deleteLevel,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useLevelService();

  const getInitialLevelFormState = (): Level => ({
    id: "",
    levelName: "",
    schoolId: "",
    nextTermFee: 0,
    order: nextOrder,
    adminId: "",
    createdAt: "",
    updatedAt: "",
  });

  const initialPromotionRuleFormState: PromotionRule = {
    id: "",
    schoolId: "",
    createdAt: "",
    updatedAt: "",
    minimumAverageScore: 0,
    maximumPositionInClass: 0,
    minimumAttendancePercentage: 0,
    requireAllSubjectsPassed: false,
    minimumSubjectScore: 0,
    autoPromoteOnSessionChange: false,
    requireManualApproval: false,
    levelId: levelToEdit?.id || "",
  };

  const colors = useThemeColors();
  const { users, promotionRules } = useSchoolsContext();
  const icons = useIcons();

  const [levelFormState, setLevelFormState] = useState<Level>(
    getInitialLevelFormState()
  );
  const [promotionRuleForm, setPromotionRuleForm] = useState(
    initialPromotionRuleFormState
  );
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const [enablePromotionRules, setEnablePromotionRules] = useState(false);

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    levelName: { type: "text" as const, required: true, maxLength: 50 },
    order: { type: "number" as const, required: true },
    nextTermFee: { type: "number" as const, required: false },
  } as const;

  const promotionFieldConfigs = {
    minimumAverageScore: {
      type: "number" as const,
      required: false,
      min: 0,
      max: 100,
    },
    maximumPositionInClass: {
      type: "number" as const,
      required: false,
      min: 0,
    },
    minimumAttendancePercentage: {
      type: "number" as const,
      required: false,
      min: 0,
      max: 100,
    },
    minimumSubjectScore: {
      type: "number" as const,
      required: false,
      min: 0,
      max: 100,
    },
    requireAllSubjectsPassed: { type: "boolean" as const, required: false },
    autoPromoteOnSessionChange: { type: "boolean" as const, required: false },
    requireManualApproval: { type: "boolean" as const, required: false },
  } as const;

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: levelToEdit || getInitialLevelFormState(),
      currentData: levelFormState,
      isEditMode: !!levelToEdit,
    });

  const [initialEnablePromotionRules, setInitialEnablePromotionRules] =
    useState(false);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const hasExistingRule =
        !!levelToEdit &&
        !!promotionRules.find(
          (promotionRule) => promotionRule.levelId === levelToEdit.id
        );
      setInitialEnablePromotionRules(hasExistingRule);
      setEnablePromotionRules(hasExistingRule);
    }
  }, [isOpen, levelToEdit, promotionRules]);

  const { hasChanges: hasPromotionRuleChanges } = useFormChanges({
    fields: promotionFieldConfigs,
    initialData: levelToEdit
      ? promotionRules.find(
          (promotionRule) => promotionRule.levelId === levelToEdit.id
        ) || initialPromotionRuleFormState
      : initialPromotionRuleFormState,
    currentData: promotionRuleForm,
    isEditMode: !!levelToEdit,
  });

  // Consider changes in enablePromotionRules toggle
  const hasPromotionToggleChanged =
    initialEnablePromotionRules !== enablePromotionRules;

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setLevelFormState,
    setTouchedFields,
    numberFields: [
      "nextTermFee",
      "order",
      "minimumAverageScore",
      "maximumPositionInClass",
      "minimumAttendancePercentage",
      "minimumSubjectScore",
    ],
  });

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof Level;
    return validateField(fieldKey, value);
  };

  // Clear errors when modal opens
  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (isOpen) {
      if (levelToEdit) {
        setLevelFormState(levelToEdit);
        // Load promotion rule data for existing level
        const rule = promotionRules.find(
          (promotionRule) => promotionRule.levelId === levelToEdit.id
        );
        if (rule) {
          setPromotionRuleForm(rule);
        } else {
          setPromotionRuleForm(initialPromotionRuleFormState);
        }
      } else {
        setLevelFormState(getInitialLevelFormState());
        setPromotionRuleForm(initialPromotionRuleFormState);
      }
      setTouchedFields(new Set());
    }
  }, [levelToEdit, isOpen, promotionRules]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      levelFormState[fieldName as keyof Level]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      levelFormState[fieldName as keyof Level]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  // Combined change detection
  const hasAnyChanges =
    hasChanges ||
    (enablePromotionRules && hasPromotionRuleChanges) ||
    hasPromotionToggleChanged;

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasAnyChanges) return;

    const data: Level = {
      levelName: levelFormState.levelName,
      nextTermFee: levelFormState.nextTermFee,
      order: levelFormState.order,
      adminId: levelFormState.adminId,
      id: "",
      schoolId: "",
      createdAt: "",
      updatedAt: "",
    };

    if (enablePromotionRules) {
      data.promotionRule = {
        id: "",
        schoolId: "",
        createdAt: "",
        updatedAt: "",
        levelId: levelFormState.id,
        minimumAverageScore: promotionRuleForm.minimumAverageScore,
        maximumPositionInClass: promotionRuleForm.maximumPositionInClass,
        minimumAttendancePercentage:
          promotionRuleForm.minimumAttendancePercentage,
        requireAllSubjectsPassed: promotionRuleForm.requireAllSubjectsPassed,
        minimumSubjectScore: promotionRuleForm.minimumSubjectScore,
        autoPromoteOnSessionChange:
          promotionRuleForm.autoPromoteOnSessionChange,
        requireManualApproval: promotionRuleForm.requireManualApproval,
      };
    }

    if (levelToEdit) {
      const response = await updateLevelWithPromotionRule(levelToEdit.id, data);
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createLevelWithPromotionRule(data);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDeleteLevel = async () => {
    if (!levelToEdit) return;

    const response = await deleteLevel(levelToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasAnyChanges && levelToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const userOptions = users.map((user) => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.id,
  }));

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDeleteLevel}
        deleteMessage="All related level records and student assignments will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasAnyChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasAnyChanges || hasValidationErrorsValue || isLoading}
        title={levelToEdit ? "Edit Level" : "Add New Level"}
        icon={icons.main.level}
        mode={levelToEdit ? "edit" : "add"}
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
        actionText={levelToEdit ? "Save Changes" : "Save Level"}
      >
        {/* Show only API/server errors */}
        {errors.length > 0 && (
          <VStack gap={2} mb={4} align={"stretch"}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} tight />
            ))}
          </VStack>
        )}

        <Tabs.Root
          value={activeTab.toString()}
          onValueChange={(details) => setActiveTab(parseInt(details.value))}
        >
          <Tabs.List mb={4}>
            <Tabs.Trigger value="0">
              <HStack gap={2}>
                <Icon as={icons.main.level} />
                <Text>Level Settings</Text>
              </HStack>
            </Tabs.Trigger>
            <Tabs.Trigger value="1">
              <HStack gap={2}>
                <Icon as={icons.main.promotion} />
                <Text>Promotion Rules</Text>
                <Badge
                  colorPalette={
                    enablePromotionRules
                      ? hasPromotionRuleChanges || hasPromotionToggleChanged
                        ? "yellow"
                        : "green"
                      : "orange"
                  }
                  variant="subtle"
                  fontSize="xs"
                >
                  {enablePromotionRules
                    ? hasPromotionRuleChanges || hasPromotionToggleChanged
                      ? "Pending Save"
                      : "Configured"
                    : "Not Set"}
                </Badge>
              </HStack>
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="0">
            <VStack gap={6} align="stretch">
              {/* Level Information Section */}
              <Box>
                <InlineSeparator label="Level Information" />

                <VStack gap={4}>
                  <TextInput
                    label="Level Name"
                    name="levelName"
                    type="text"
                    value={levelFormState.levelName}
                    onChange={handleFieldChange}
                    placeholder="e.g. Primary"
                    required
                    error={
                      showFieldError("levelName")
                        ? getFieldError("levelName")
                        : ""
                    }
                    invalid={showFieldError("levelName")}
                  />

                  <SelectInput
                    label="Level Admin"
                    name="adminId"
                    value={levelFormState.adminId}
                    onChange={handleFieldChange}
                    collection={userOptions}
                    placeholder="Select Level Admin"
                    error={
                      showFieldError("adminId") ? getFieldError("adminId") : ""
                    }
                    invalid={showFieldError("adminId")}
                  />

                  <CustomNumberInput
                    label="Next Term Fee"
                    value={levelFormState.nextTermFee.toString()}
                    onChange={(value) => {
                      setLevelFormState((prev) => ({
                        ...prev,
                        nextTermFee: parseFloat(value) || 0,
                      }));
                      setTouchedFields((prev) =>
                        new Set(prev).add("nextTermFee")
                      );
                    }}
                    min={0}
                    error={
                      showFieldError("nextTermFee")
                        ? getFieldError("nextTermFee")
                        : ""
                    }
                    invalid={showFieldError("nextTermFee")}
                  />

                  <CustomNumberInput
                    label="Order"
                    value={levelFormState.order.toString()}
                    onChange={(value) => {
                      setLevelFormState((prev) => ({
                        ...prev,
                        order: parseInt(value) || 0,
                      }));
                      setTouchedFields((prev) => new Set(prev).add("order"));
                    }}
                    min={1}
                    error={
                      showFieldError("order") ? getFieldError("order") : ""
                    }
                    invalid={showFieldError("order")}
                    info={
                      <Info content="The order of the level in a list of levels. This helps with arranging the levels the way you want." />
                    }
                  />
                </VStack>
              </Box>
            </VStack>
          </Tabs.Content>

          <Tabs.Content value="1">
            <VStack gap={6} align="stretch">
              {/* Promotion Rule Configuration */}
              <Box>
                {/* Show toggle for new entries or when no promotion rule exists */}
                {(!levelToEdit ||
                  !promotionRules.find(
                    (promotionRule) => promotionRule.levelId === levelToEdit?.id
                  )) && (
                  <>
                    <InlineSeparator label="Promotion Rules" />
                    <Box mb={6}>
                      <HStack justify="space-between" w="full">
                        <VStack align="start" gap={1}>
                          <Text fontSize="md" fontWeight="medium">
                            Enable Promotion Rules
                          </Text>
                          <Text fontSize="sm" color={colors.text.secondary}>
                            Configure promotion criteria for this level
                          </Text>
                        </VStack>
                        <CustomSwitch
                          checked={enablePromotionRules}
                          onChange={() =>
                            setEnablePromotionRules(!enablePromotionRules)
                          }
                        />
                      </HStack>
                    </Box>
                  </>
                )}

                {/* Promotion Rule Form - Only show when enabled */}
                {enablePromotionRules && (
                  <Box>
                    {/* Basic Criteria Section */}
                    <Box>
                      <InlineSeparator label="Basic Criteria" />

                      <VStack gap={4}>
                        <HStack gap={4} w="full" align={"start"}>
                          <CustomNumberInput
                            label="Minimum Average Score (%)"
                            value={promotionRuleForm.minimumAverageScore.toString()}
                            onChange={(value) =>
                              setPromotionRuleForm((prev) => ({
                                ...prev,
                                minimumAverageScore: parseFloat(value) || 0,
                              }))
                            }
                            min={0}
                            max={100}
                          />

                          <CustomNumberInput
                            label="Maximum Position in Class"
                            value={
                              promotionRuleForm.maximumPositionInClass?.toString() ||
                              ""
                            }
                            onChange={(value) =>
                              setPromotionRuleForm((prev) => ({
                                ...prev,
                                maximumPositionInClass: value
                                  ? parseFloat(value)
                                  : 0,
                              }))
                            }
                            min={0}
                          />
                        </HStack>

                        <HStack gap={4} w="full" align={"start"}>
                          <CustomNumberInput
                            label="Minimum Attendance (%)"
                            value={promotionRuleForm.minimumAttendancePercentage.toString()}
                            onChange={(value) =>
                              setPromotionRuleForm((prev) => ({
                                ...prev,
                                minimumAttendancePercentage:
                                  parseFloat(value) || 0,
                              }))
                            }
                            min={0}
                            max={100}
                          />

                          <CustomNumberInput
                            label="Minimum Subject Score (%)"
                            value={
                              promotionRuleForm.minimumSubjectScore?.toString() ||
                              ""
                            }
                            onChange={(value) =>
                              setPromotionRuleForm((prev) => ({
                                ...prev,
                                minimumSubjectScore: value
                                  ? parseFloat(value)
                                  : undefined,
                              }))
                            }
                            min={0}
                            max={100}
                          />
                        </HStack>
                      </VStack>
                    </Box>

                    <Box
                      borderTop="1px"
                      borderColor={colors.border.secondary}
                      pt={4}
                    />

                    {/* Advanced Settings Section */}
                    <Box>
                      <InlineSeparator label="Advanced Settings" />

                      <VStack gap={4}>
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Require All Subjects Passed
                          </Text>
                          <CustomSwitch
                            checked={promotionRuleForm.requireAllSubjectsPassed}
                            onChange={() =>
                              setPromotionRuleForm((prev) => ({
                                ...prev,
                                requireAllSubjectsPassed:
                                  !prev.requireAllSubjectsPassed,
                              }))
                            }
                          />
                        </HStack>

                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Auto Promote on Session Change
                          </Text>
                          <CustomSwitch
                            checked={
                              promotionRuleForm.autoPromoteOnSessionChange
                            }
                            onChange={() =>
                              setPromotionRuleForm((prev) => ({
                                ...prev,
                                autoPromoteOnSessionChange:
                                  !prev.autoPromoteOnSessionChange,
                              }))
                            }
                          />
                        </HStack>

                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Require Manual Approval
                          </Text>
                          <CustomSwitch
                            checked={promotionRuleForm.requireManualApproval}
                            onChange={() =>
                              setPromotionRuleForm((prev) => ({
                                ...prev,
                                requireManualApproval:
                                  !prev.requireManualApproval,
                              }))
                            }
                          />
                        </HStack>
                      </VStack>
                    </Box>
                  </Box>
                )}
              </Box>
            </VStack>
          </Tabs.Content>
        </Tabs.Root>
      </CustomModal>
    </>
  );
};

export default LevelModal;
